import React, { useCallback, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';

//  Components
import CommonTable from '../CommonTable/CommonTable';
import TableAction from '../CommonTable/TableAction';
import CustomConfirmModal from '../Common/CustomConfirmModal/CustomConfirmModal';
import CustomPagination from '../CustomPagination/CustomPagination';
// Helpers / HOC / GraphQL / Actions / Messages
import { withQuery } from '../../hoc/withGraphql';
import { getAllServicePlans } from '../../lib/graphql';
import { deleteServicePlan } from '../../actions/ServicePlans/deleteServicePlan';
import debounce from '../../helpers/debounce';
import { updateServicePlansStatus } from '../../actions/ServicePlans/updateServicePlanStatus';
import Link from '../Link/Link';
import withStyles from 'isomorphic-style-loader/withStyles';
import { compose } from 'redux';
import cs from '../../components/commonStyle.css';
import CurrencyConverter from '../CurrencyConverter/CurrencyConverter';
import messages from '../../locale/jsonMessages';
import { getServicePlanValue } from '../../helpers/GetDynamicItemName/dynamicHomeValue';

const ServicePlanManagement = (props) => {
    const { formatMessage, locale } = useIntl();
    const dispatch = useDispatch();

    const { data: { getAllServicePlans = {}, refetch, loading } } = props;

    const [modalState, setModalState] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [searchKey, setSearchKey] = useState('');
    const [userType, setUserType] = useState('');

    const thead = () => [
        { data: formatMessage(messages.idLabel) },
        { data: formatMessage(messages.planLabel) },
        { data: formatMessage(messages.priceLabel) },
        { data: `${formatMessage(messages.duration)} (${formatMessage(messages.monthsLabel)})` },
        { data: formatMessage(messages.userTypeLabel) },
        { data: formatMessage(messages.actionLabel) }
    ];

    const userTypeFilter = () => [
        { value: '', label: formatMessage(messages.allUserTypeLabel) },
        { value: 'renter', label: formatMessage(messages.guest) },
        { value: 'owner', label: formatMessage(messages.host) }
    ];

    const setStateVariable = (variables) => {
        if (variables.currentPage !== undefined) {
            setCurrentPage(variables.currentPage);
        }
        setSearchKey(variables?.searchKey);
        setUserType(variables?.userType);
    };

    const deleteChange = useCallback((id) => {
        setModalState(true);
        setSelectedId(id);
    }, []);

    const handleSearchChange = debounce((value) => {
        const variables = { currentPage: 1, searchKey: value, userType };
        setStateVariable(variables);
        refetch(variables);
    }, 250);

    const handleDropDown = (e) => {
        const variables = { userType: e, currentPage: 1, searchKey };
        setStateVariable(variables);
        refetch(variables)
    };


    const handleDelete = async (id, currentPage) => {
        setModalState(false);
        await dispatch(deleteServicePlan(id));

        const pageResults = getAllServicePlans?.results || [];
        const updatedPage =
            currentPage > 1 && pageResults.length === 1
                ? currentPage - 1
                : currentPage;

        setStateVariable({ currentPage: updatedPage, searchKey: searchKey ? searchKey : '' });
        refetch({ currentPage: updatedPage, searchKey: searchKey ? searchKey : '' });
    };

    const paginationData = (newPage) => {
        const variables = { currentPage: newPage, searchKey, userType };
        setStateVariable(variables);
        refetch(variables);
    };


    const handleStatusChange = async (id, isEnable) => {
        await dispatch(updateServicePlansStatus(id, isEnable));
        refetch();
    };

    const tbody = () => getAllServicePlans?.results?.map((value, key) => {
        let { planName } = getServicePlanValue(value, locale);
        return {
            id: key,
            data: [
                { data: value?.id },
                {
                    data: (
                        <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween)}>
                            <Link to={`/siteadmin/service-plans/edit/${value?.id}`} className={cs.siteLinkColor}>
                                {planName}
                            </Link>
                            <TableAction
                                onClickDelete={() => deleteChange(value.id)}
                                showDelete={true}
                                showView={true}
                                showEdit={true}
                                editLink={`/siteadmin/service-plans/edit/${value?.id}`}
                            />
                        </div>
                    )
                },
                {
                    data: (
                        <div className={cx(cs.displayFlex, cs.alignCenter, cs.spaceBetween)}>
                            <CurrencyConverter amount={value?.price} from={value?.currency} />
                        </div>
                    ),
                },
                {
                    data: (
                        <div>
                            {value?.duration}{' '}
                            {value?.duration == 1}
                        </div>
                    )
                },
                {
                    data: (
                        <div>
                            {value?.userType === 'renter'
                                ? formatMessage(messages.guest)
                                : formatMessage(messages.hosting)}
                        </div>
                    )
                },
                {
                    data: (
                        <div>
                            <select value={value?.isEnable} className='commonTableInsideSelect'
                                onChange={(e) => handleStatusChange(value?.id, value?.isEnable)} >
                                <option value={true}>
                                    {formatMessage(messages.activeLabel)}
                                </option>
                                <option value={false}>
                                    {formatMessage(messages.inActiveLabel)}
                                </option>
                            </select>
                        </div>
                    )
                }
            ]
        }
    });

    return (
        <div>
            {modalState && (
                <CustomConfirmModal
                    showModel={modalState}
                    title={formatMessage(messages.deleteServicePlansTitle)}
                    bodyText={formatMessage(messages.deleteServicePlansConfirm)}
                    closeModal={() => setModalState(false)}
                    popupButtonLeftFunction={() => setModalState(false)}
                    popupButtonLeftName={formatMessage(messages.cancel)}
                    popupButtonRightFunction={() =>
                        handleDelete(selectedId, currentPage)
                    }
                    popupButtonRightName={formatMessage(messages.confirmLabel)}
                />
            )}

            {
                !loading && <CommonTable
                    thead={thead}
                    tbody={tbody}
                    title={formatMessage(messages.servicePlans)}
                    isSearch
                    isLink
                    onSearch={(e) => handleSearchChange(e?.target?.value)}
                    href="/siteadmin/service-plans/add"
                    redirectionLabel={formatMessage(messages.addNewLabel)}
                    userTypeFilter={userTypeFilter()}
                    handleUserChange={handleDropDown}
                />
            }
            {
                !loading && getAllServicePlans?.results?.length > 0
                && <div>
                    <CustomPagination
                        total={getAllServicePlans?.count}
                        currentPage={currentPage}
                        defaultCurrent={1}
                        defaultPageSize={10}
                        change={paginationData}
                        paginationLabel={formatMessage(messages.servicePlans)}
                    />
                </div>
            }
        </div>
    );
};

export default compose(
    withStyles(cs),
    withQuery(getAllServicePlans, {
        options: (props) => ({
            variables: {
                currentPage: props.currentPage,
                searchList: props.searchKey,
                userType: props.userType
            },
            fetchPolicy: 'network-only',
            ssr: false
        })
    }))(ServicePlanManagement);
